import React, { useEffect, useState, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Row,
  Col,
  Input,
  Card,
  CardBody,
  Button,
  Label,
  FormGroup,
  Badge,
  Table
} from "reactstrap";

import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import "../../assets/custom css/loadoverlay.scss";
import Alerts from "../../components/Alerts/alerts";

import ComplaintsService from "./complaints.service";
import AuthService from "../Authentication/auth.service";
import UserService from "../Users Management/users.service";

const ViewComplaint = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const complaintId = searchParams.get('id');
  
  const [loading, setLoading] = useState(false);
  const [complaint, setComplaint] = useState(null);
  const [comments, setComments] = useState([]); // Separate state for discussion/comments
  const [replyMessage, setReplyMessage] = useState('');
  const [replyStatus, setReplyStatus] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [systemUsers, setSystemUsers] = useState([]);
  const [assignedTo, setAssignedTo] = useState('');
  const [recipientCustomerEmails, setRecipientCustomerEmails] = useState([]);
  const [recipientEmailInput, setRecipientEmailInput] = useState("");
  const [recipientInputFocused, setRecipientInputFocused] = useState(false);

  useEffect(() => {
    if (complaintId) {
      fetchComplaint();
      fetchDiscussion(); // Fetch discussion separately
      fetchStatuses();
      fetchPriorities();
      fetchSystemUsers();
    }
  }, [complaintId]);

  const fetchComplaint = () => {
    setLoading(true);
    ComplaintsService.getComplaint(complaintId).then((response) => {
      setLoading(false);
      if (response.data.status && response.data.data.length > 0) {
        const complaintData = response.data.data[0];
        setComplaint(complaintData);
        setReplyStatus(complaintData.status);
        setRecipientCustomerEmails(complaintData.recipient_customer_emails || []);
        // Set currently assigned admin for dropdown
        setAssignedTo(
          (complaintData.assigned_to && complaintData.assigned_to.toString()) ||
          (complaintData.assigned_admin_details && complaintData.assigned_admin_details._id) ||
          ''
        );
      } else {
        Alerts.swalErrorAlert("Complaint not found");
        navigate('/complaints-list');
      }
    }, (error) => {
      setLoading(false);
      Alerts.swalErrorAlert("Failed to load complaint");
    });
  };

  const fetchDiscussion = () => {
    // Fetch discussion/comments separately for optimization
    ComplaintsService.getComplaintDiscussion(complaintId).then((response) => {
      if (response.data.status && response.data.data.length > 0) {
        setComments(response.data.data[0].comments || []);
      }
    }, (error) => {
      // Silent fail - discussion will just be empty
      setComments([]);
    });
  };

  const fetchStatuses = () => {
    ComplaintsService.getStatuses().then((response) => {
      if (response.data.status) {
        setStatuses(response.data.data);
      }
    });
  };

  const fetchPriorities = () => {
    ComplaintsService.getPriorities().then((response) => {
      if (response.data.status) {
        setPriorities(response.data.data);
      }
    });
  };

  const fetchSystemUsers = () => {
    UserService.getAllUsers().then((response) => {
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
      } else if (response.data.status || response.data.data) {
        setSystemUsers(response.data.data || []);
      }
    }).catch(() => {
      // Silent fail for dropdown; main page should still work
    });
  };

  // Convert files to Base64 (similar to profile_image pattern)
  const convertFilesToBase64 = (files) => {
    return Promise.all(
      Array.from(files).map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            resolve({
              filename: file.name,
              data: reader.result // Base64 data URL: "data:mime/type;base64,..."
            });
          };
          reader.onerror = (error) => reject(error);
        });
      })
    );
  };

  const handleAddReply = async () => {
    if (!replyMessage.trim()) {
      Alerts.swalErrorAlert("Please enter a message");
      return;
    }

    setLoading(true);
    
    // Convert files to Base64 before sending
    let base64Attachments = [];
    if (replyFiles && replyFiles.length > 0) {
      try {
        base64Attachments = await convertFilesToBase64(replyFiles);
      } catch (error) {
        setLoading(false);
        Alerts.swalErrorAlert("Failed to convert files to Base64");
        return;
      }
    }

    const formData = {
      complaint_id: complaintId,
      message: replyMessage,
      status: replyStatus,
      attachments: base64Attachments
    };

    ComplaintsService.addReply(formData).then((response) => {
      setLoading(false);
      if (response.data.status) {
        Alerts.swalSuccessAlert("Reply added successfully");
        setReplyMessage('');
        setReplyFiles([]);
        setShowReplyForm(false);
        // Refresh only discussion instead of full complaint
        fetchDiscussion();
        // Also refresh complaint to get updated status if changed
        fetchComplaint();
      } else {
        Alerts.swalErrorAlert(response.data.message || "Failed to add reply");
      }
    }, (error) => {
      setLoading(false);
      Alerts.swalErrorAlert("Failed to add reply");
    });
  };

  const handleUpdateStatus = (newStatus) => {
    setLoading(true);
    ComplaintsService.updateComplaint({
      complaint_id: complaintId,
      status: newStatus
    }).then((response) => {
      setLoading(false);
      if (response.data.status) {
        Alerts.swalSuccessAlert("Status updated successfully");
        fetchComplaint();
      } else {
        Alerts.swalErrorAlert(response.data.message || "Failed to update status");
      }
    }, (error) => {
      setLoading(false);
      Alerts.swalErrorAlert("Failed to update status");
    });
  };

  const handleUpdatePriority = (newPriority) => {
    setLoading(true);
    ComplaintsService.updateComplaint({
      complaint_id: complaintId,
      priority: newPriority
    }).then((response) => {
      setLoading(false);
      if (response.data.status) {
        Alerts.swalSuccessAlert("Priority updated successfully");
        fetchComplaint();
      } else {
        Alerts.swalErrorAlert(response.data.message || "Failed to update priority");
      }
    }, (error) => {
      setLoading(false);
      Alerts.swalErrorAlert("Failed to update priority");
    });
  };

  const handleUpdateAssignedTo = (newAssignedTo) => {
    setAssignedTo(newAssignedTo);
    if (!newAssignedTo) return;

    setLoading(true);
    ComplaintsService.updateComplaint({
      complaint_id: complaintId,
      assigned_to: newAssignedTo
    }).then((response) => {
      setLoading(false);
      if (response.data.status) {
        Alerts.swalSuccessAlert("Assignment updated successfully");
        fetchComplaint();
      } else {
        Alerts.swalErrorAlert(response.data.message || "Failed to update assignment");
      }
    }, () => {
      setLoading(false);
      Alerts.swalErrorAlert("Failed to update assignment");
    });
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

  const addRecipientEmail = (rawEmail) => {
    const email = (rawEmail || "").trim().toLowerCase();
    if (!email) return;
    if (!isValidEmail(email)) {
      Alerts.swalErrorAlert("Please enter a valid recipient email");
      return;
    }
    setRecipientCustomerEmails((prev) => {
      if (prev.includes(email)) return prev;
      return [...prev, email];
    });
    setRecipientEmailInput("");
  };

  const removeRecipientEmail = (emailToRemove) => {
    setRecipientCustomerEmails((prev) => prev.filter((email) => email !== emailToRemove));
  };

  const handleRecipientEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addRecipientEmail(recipientEmailInput);
    }
    if (e.key === "Backspace" && !recipientEmailInput && recipientCustomerEmails.length > 0) {
      const emails = [...recipientCustomerEmails];
      const lastEmail = emails.pop();
      setRecipientCustomerEmails(emails);
      setRecipientEmailInput(lastEmail || "");
    }
  };

  const handleUpdateRecipientEmails = () => {
    if (!recipientCustomerEmails || recipientCustomerEmails.length === 0) {
      Alerts.swalErrorAlert("Please add at least one recipient customer email");
      return;
    }
    setLoading(true);
    ComplaintsService.updateComplaint({
      complaint_id: complaintId,
      recipient_customer_emails: recipientCustomerEmails
    }).then((response) => {
      setLoading(false);
      if (response.data.status) {
        Alerts.swalSuccessAlert("Recipient emails updated successfully");
        fetchComplaint();
      } else {
        Alerts.swalErrorAlert(response.data.message || "Failed to update recipient emails");
      }
    }, () => {
      setLoading(false);
      Alerts.swalErrorAlert("Failed to update recipient emails");
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      'New': 'primary',
      'In Progress': 'warning',
      'Pending Customer Response': 'info',
      'Resolved': 'success',
      'Closed': 'secondary'
    };
    return <Badge color={colors[status] || 'secondary'}>{status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'Low': 'success',
      'Medium': 'warning',
      'High': 'danger',
      'Critical': 'dark'
    };
    return <Badge color={colors[priority] || 'secondary'}>{priority}</Badge>;
  };

  const getUserNameById = (id) => {
    if (!id || !systemUsers || systemUsers.length === 0) return id;
    const idStr = id.toString();
    const user = systemUsers.find(u => u._id === idStr || (u._id && u._id.toString && u._id.toString() === idStr));
    return user ? user.name : id;
  };

  const formatActivityDetails = (details, action, performedByName) => {
    if (!details) return <span style={{ color: '#6c757d', fontStyle: 'italic' }}>No details available</span>;
    
    try {
      // If details is already an object, use it; otherwise parse it
      const detailsObj = typeof details === 'string' ? JSON.parse(details) : details;
      
      // Handle Reply Added - just show simple message
      if (action === 'Reply Added' || action === 'Reply added') {
        return (
          <span style={{ color: '#495057' }}>
            <i className="pe-7s-comment me-2" style={{ color: '#6c757d' }}></i>
            Reply added by <strong>{performedByName || detailsObj.performed_by_name || 'Admin'}</strong>
          </span>
        );
      }
      
      const parts = [];
      
      // Handle Status Updates
      if (detailsObj.old_status && detailsObj.new_status) {
        parts.push(
          <span key="status" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '12px', marginBottom: '4px' }}>
            <i className="pe-7s-flag me-2" style={{ color: '#6c757d' }}></i>
            Status changed from <Badge color="danger" style={{ margin: '0 4px' }}>{detailsObj.old_status}</Badge> to <Badge color="success" style={{ margin: '0 4px' }}>{detailsObj.new_status}</Badge>
          </span>
        );
      }
      
      // Handle Priority Updates
      if (detailsObj.old_priority && detailsObj.new_priority) {
        parts.push(
          <span key="priority" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '12px', marginBottom: '4px' }}>
            <i className="pe-7s-star me-2" style={{ color: '#6c757d' }}></i>
            Priority changed from <Badge color="danger" style={{ margin: '0 4px' }}>{detailsObj.old_priority}</Badge> to <Badge color="success" style={{ margin: '0 4px' }}>{detailsObj.new_priority}</Badge>
          </span>
        );
      }
      
      // Handle Complaint Created
      if (detailsObj.status && detailsObj.priority && detailsObj.category && action === 'Complaint Created') {
        parts.push(
          <span key="status" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '12px', marginBottom: '4px' }}>
            <i className="pe-7s-flag me-2" style={{ color: '#6c757d' }}></i>
            Status: <strong>{detailsObj.status}</strong>
          </span>
        );
        parts.push(
          <span key="priority" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '12px', marginBottom: '4px' }}>
            <i className="pe-7s-star me-2" style={{ color: '#6c757d' }}></i>
            Priority: <strong>{detailsObj.priority}</strong>
          </span>
        );
        parts.push(
          <span key="category" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '12px', marginBottom: '4px' }}>
            <i className="pe-7s-folder me-2" style={{ color: '#6c757d' }}></i>
            Category: <strong>{detailsObj.category}</strong>
          </span>
        );
      }
      
      // Handle other individual fields (if not part of updates)
      if (!detailsObj.old_status && !detailsObj.old_priority) {
        // Handle reassignment (assigned_to changes)
        if (detailsObj.old_assigned_to && detailsObj.new_assigned_to) {
          const oldName = getUserNameById(detailsObj.old_assigned_to);
          const newName = getUserNameById(detailsObj.new_assigned_to);
          parts.push(
            <span key="assigned_to" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '12px', marginBottom: '4px' }}>
              <i className="pe-7s-user me-2" style={{ color: '#6c757d' }}></i>
              Assigned changed from <strong style={{ margin: '0 4px' }}>{oldName}</strong> to <strong style={{ margin: '0 4px' }}>{newName}</strong>
            </span>
          );
        }

        if (detailsObj.category && action !== 'Complaint Created') {
          parts.push(
            <span key="category" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '12px', marginBottom: '4px' }}>
              <i className="pe-7s-folder me-2" style={{ color: '#6c757d' }}></i>
              Category: <strong>{detailsObj.category}</strong>
            </span>
          );
        }
        
        if (detailsObj.priority && !detailsObj.old_priority) {
          parts.push(
            <span key="priority-only" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '12px', marginBottom: '4px' }}>
              <i className="pe-7s-star me-2" style={{ color: '#6c757d' }}></i>
              Priority: <strong>{detailsObj.priority}</strong>
            </span>
          );
        }
        
        if (detailsObj.status && !detailsObj.old_status) {
          parts.push(
            <span key="status-only" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '12px', marginBottom: '4px' }}>
              <i className="pe-7s-flag me-2" style={{ color: '#6c757d' }}></i>
              Status: <strong>{detailsObj.status}</strong>
            </span>
          );
        }
      }
      
      // If no specific formatting found, show a simplified version
      if (parts.length === 0) {
        const filteredEntries = Object.entries(detailsObj)
          .filter(([key]) => key !== 'performed_by_name');
        
        if (filteredEntries.length === 0) {
          return <span style={{ color: '#6c757d', fontStyle: 'italic' }}>No additional details</span>;
        }
        
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {filteredEntries.map(([key, value]) => (
              <span key={key} style={{ display: 'inline-flex', alignItems: 'center' }}>
                <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {String(value)}
              </span>
            ))}
          </div>
        );
      }
      
      return <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>{parts}</div>;
    } catch (error) {
      // If parsing fails, try to show it as plain text
      return <span style={{ color: '#495057' }}>{typeof details === 'string' ? details : 'Unable to display details'}</span>;
    }
  };

  if (!complaint) {
    return (
      <Fragment>
        <div className="app-main__outer">
          <div className="app-main__inner">
            <LoadingOverlay active={loading} spinner={<DNALoader />}>
              <div>Loading...</div>
            </LoadingOverlay>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .conversation-thread::-webkit-scrollbar {
          width: 8px;
        }
        .conversation-thread::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .conversation-thread::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        .conversation-thread::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <div className="app-page-title">
            <div className="page-title-wrapper justify-content-left">
              <div className="page-title-heading fw-bold">
                <div className="page-title-icon">
                  <i className="pe-7s-note2 icon-gradient bg-happy-green" />
                </div>
                <div>
                  {t('View Complaint')}
                  <div className="page-title-subheading">
                    {t('Complaint Reference:')} {complaint.reference_number}
                  </div>
                </div>
              </div>
              <div className="page-title-actions">
                <button type="button" onClick={() => navigate('/complaints-list')} className="btn btn-outline-success btn-hover-shine me-3">
                  {t('Back to List')}
                </button>
              </div>
            </div>
          </div>
          
          <div className="d-flex justify-content-center">
            <div className="col-md-12">
              <LoadingOverlay tag="div" active={loading}
                styles={{ overlay: (base) => ({ ...base }) }}
                spinner={<DNALoader />}>
                
                {/* Complaint Information Card */}
                <Card className="main-card mb-3">
                  <CardBody>
                    <div className="d-flex align-items-center mb-4" style={{ borderBottom: '2px solid #e9ecef', paddingBottom: '15px' }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '12px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        marginRight: '15px'
                      }}>
                        <i className="pe-7s-note2"></i>
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 className="mb-1" style={{ fontWeight: '600', color: '#212529' }}>
                          {complaint.subject}
                        </h4>
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '5px' }}>
                          <span style={{ fontSize: '13px', color: '#6c757d' }}>
                            <i className="pe-7s-id me-1"></i>
                            <strong>Ref:</strong> {complaint.reference_number}
                          </span>
                          <span style={{ fontSize: '13px', color: '#6c757d' }}>
                            <i className="pe-7s-clock me-1"></i>
                            {new Date(complaint.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        {getPriorityBadge(complaint.priority)}
                        {getStatusBadge(complaint.status)}
                      </div>
                    </div>

                    <Row style={{ display: 'flex' }}>
                      <Col md={6} style={{ display: 'flex' }}>
                        <div className="mb-3" style={{
                          padding: '15px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #e9ecef',
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600' }}>
                            <i className="pe-7s-user me-1"></i>
                            Customer Information
                          </div>
                          <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>
                            {complaint.customer_details?.company_name || complaint.customer_name || 'N/A'}
                          </div>
                          <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '4px' }}>
                            {complaint.customer_details?.email || complaint.customer_email || 'N/A'}
                          </div>
                        </div>
                      </Col>
                      <Col md={6} style={{ display: 'flex' }}>
                        <div className="mb-3" style={{
                          padding: '15px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #e9ecef',
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600' }}>
                            <i className="pe-7s-info me-1"></i>
                            Complaint Details
                          </div>
                          <div style={{ fontSize: '13px', color: '#212529', marginBottom: '6px' }}>
                            <strong>Category:</strong> {complaint.category}
                          </div>
                          <div style={{ fontSize: '13px', color: '#212529', marginBottom: '6px' }}>
                            <strong>Assigned To:</strong> {complaint.assigned_admin_details?.name || 'N/A'}
                          </div>
                          {complaint.order_id && (
                            <div style={{ fontSize: '13px', color: '#212529' }}>
                              <strong>Related Order:</strong> {complaint.order_details?.order_no || complaint.order_id}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <div style={{
                          padding: '20px',
                          backgroundColor: '#ffffff',
                          borderRadius: '8px',
                          border: '1px solid #e9ecef',
                          borderLeft: '4px solid #007bff'
                        }}>
                          <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '10px', textTransform: 'uppercase', fontWeight: '600' }}>
                            <i className="pe-7s-note me-1"></i>
                            Description
                          </div>
                          <p style={{ margin: 0, color: '#212529', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>
                            {complaint.description}
                          </p>
                        </div>
                      </Col>
                    </Row>

                    {complaint.attachments && complaint.attachments.length > 0 && (
                      <Row className="mb-3">
                        <Col md={12}>
                          <div style={{
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef'
                          }}>
                            <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px', textTransform: 'uppercase', fontWeight: '600' }}>
                              <i className="pe-7s-paperclip me-1"></i>
                              Attachments ({complaint.attachments.length})
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                              {complaint.attachments.map((attachment, index) => {
                                // Handle Base64 format (for AddCustomerReply attachments)
                                const isBase64 = attachment && attachment.data && attachment.data.startsWith('data:');
                                const attachmentUrl = isBase64 
                                  ? attachment.data 
                                  : (() => {
                                      // Handle both object format {path, filename} and string format (backward compatibility)
                                      const attachmentPath = typeof attachment === 'string' ? attachment : (attachment.path || attachment);
                                      const baseUrlForFiles = process.env.REACT_APP_LOGINURL?.replace('/api/', '/') || 'http://localhost:3005/';
                                      return attachmentPath.startsWith('http') 
                                        ? attachmentPath 
                                        : `${baseUrlForFiles}${attachmentPath}`;
                                    })();
                                const attachmentFilename = isBase64
                                  ? (attachment.filename || 'attachment')
                                  : (typeof attachment === 'string' 
                                    ? attachment.split('/').pop() 
                                    : (attachment.filename || (attachment.path ? attachment.path.split('/').pop() : 'attachment')));
                                // Handle click for Base64 attachments (open in new window properly)
                                const handleAttachmentClick = (e) => {
                                  if (isBase64) {
                                    e.preventDefault();
                                    // Convert Base64 to blob URL for more reliable opening
                                    try {
                                      const base64Data = attachment.data.split(',')[1];
                                      const mimeType = attachment.data.split(',')[0].match(/:(.*?);/)[1];
                                      const byteCharacters = atob(base64Data);
                                      const byteNumbers = new Array(byteCharacters.length);
                                      for (let i = 0; i < byteCharacters.length; i++) {
                                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                                      }
                                      const byteArray = new Uint8Array(byteNumbers);
                                      const blob = new Blob([byteArray], { type: mimeType });
                                      const blobUrl = URL.createObjectURL(blob);
                                      const newWindow = window.open(blobUrl, '_blank');
                                      if (newWindow) {
                                        // Clean up blob URL after a delay
                                        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
                                      }
                                    } catch (error) {
                                      console.error('Error opening Base64 attachment:', error);
                                      // Fallback: try direct open
                                      window.open(attachment.data, '_blank');
                                    }
                                  }
                                  // For non-Base64, let default link behavior work
                                };

                                return (
                                  <a
                                    key={index}
                                    href={isBase64 ? '#' : attachmentUrl}
                                    target={isBase64 ? undefined : "_blank"}
                                    rel={isBase64 ? undefined : "noopener noreferrer"}
                                    onClick={handleAttachmentClick}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      padding: '8px 14px',
                                      backgroundColor: '#ffffff',
                                      borderRadius: '6px',
                                      textDecoration: 'none',
                                      color: '#007bff',
                                      fontSize: '14px',
                                      border: '1px solid #dee2e6',
                                      transition: 'all 0.2s',
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                      cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#e7f3ff';
                                      e.currentTarget.style.borderColor = '#007bff';
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#ffffff';
                                      e.currentTarget.style.borderColor = '#dee2e6';
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                                    }}
                                  >
                                    <i className="pe-7s-file me-2"></i>
                                    {attachmentFilename}
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    )}
                  </CardBody>
                </Card>

                {/* Quick Actions Card */}
                <Card className="main-card mb-3">
                  <CardBody>
                    <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px', fontWeight: '600' }}>
                      <i className="pe-7s-settings me-2"></i>
                      Quick Actions
                    </div>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                            <i className="pe-7s-flag me-1"></i>
                            Update Status
                          </Label>
                          <Input
                            type="select"
                            value={complaint.status}
                            onChange={(e) => handleUpdateStatus(e.target.value)}
                            style={{ borderRadius: '6px' }}
                          >
                            {statuses.map((status, index) => (
                              <option key={index} value={status.value}>{status.name}</option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                            <i className="pe-7s-star me-1"></i>
                            Update Priority
                          </Label>
                          <Input
                            type="select"
                            value={complaint.priority}
                            onChange={(e) => handleUpdatePriority(e.target.value)}
                            style={{ borderRadius: '6px' }}
                          >
                            {priorities.map((priority, index) => (
                              <option key={index} value={priority.value}>{priority.name}</option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                            <i className="pe-7s-user me-1"></i>
                            Assigned To
                          </Label>
                          <Input
                            type="select"
                            value={assignedTo || ''}
                            onChange={(e) => handleUpdateAssignedTo(e.target.value)}
                            style={{ borderRadius: '6px' }}
                          >
                            <option value="">Select user</option>
                            {systemUsers.map((user) => (
                              <option key={user._id} value={user._id}>
                                {user.name}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                            <i className="pe-7s-mail me-1"></i>
                            Recipient Customer Emails
                          </Label>
                          <div
                            style={{
                              border: recipientInputFocused ? "1px solid #80bdff" : "1px solid #ced4da",
                              borderRadius: "6px",
                              minHeight: "38px",
                              padding: "4px 8px",
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "center",
                              gap: "6px",
                              backgroundColor: "#fff",
                              boxShadow: recipientInputFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : "none"
                            }}
                            onClick={() => document.getElementById("view-recipient-customer-emails-input")?.focus()}
                          >
                            {(recipientCustomerEmails || []).map((email) => (
                              <div
                                key={email}
                                style={{
                                  border: "1px solid #d6d8db",
                                  borderRadius: "14px",
                                  padding: "2px 8px",
                                  background: "#f1f3f5",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  maxWidth: "100%"
                                }}
                              >
                                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{email}</span>
                                <button
                                  type="button"
                                  onClick={() => removeRecipientEmail(email)}
                                  style={{
                                    border: "none",
                                    background: "transparent",
                                    color: "#dc3545",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    lineHeight: 1,
                                    padding: 0
                                  }}
                                  aria-label={`Remove ${email}`}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            <input
                              id="view-recipient-customer-emails-input"
                              type="text"
                              value={recipientEmailInput}
                              onChange={(e) => setRecipientEmailInput(e.target.value)}
                              onKeyDown={handleRecipientEmailKeyDown}
                              onBlur={() => {
                                setRecipientInputFocused(false);
                                addRecipientEmail(recipientEmailInput);
                              }}
                              onFocus={() => setRecipientInputFocused(true)}
                              placeholder={recipientCustomerEmails.length ? "" : "Type email and press Enter"}
                              style={{
                                border: "none",
                                outline: "none",
                                flex: 1,
                                minWidth: "220px",
                                padding: "4px 2px",
                                background: "transparent"
                              }}
                            />
                          </div>
                          <div className="mt-2">
                            <Button
                              color="primary"
                              size="sm"
                              onClick={handleUpdateRecipientEmails}
                              style={{ borderRadius: '6px' }}
                            >
                              Save Recipient Emails
                            </Button>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Discussion Section */}
                <Card className="main-card mb-3">
                  <CardBody>
                    <div className="d-flex align-items-center justify-content-between mb-4" style={{ borderBottom: '2px solid #e9ecef', paddingBottom: '15px' }}>
                      <div className="d-flex align-items-center">
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          marginRight: '12px'
                        }}>
                          <i className="pe-7s-chat"></i>
                        </div>
                        <div>
                          <h5 className="mb-0" style={{ fontWeight: '600', color: '#212529' }}>
                            Discussion
                          </h5>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                            {comments && comments.length > 0 
                              ? `${comments.length} ${comments.length === 1 ? 'message' : 'messages'}`
                              : 'No messages yet'
                            }
                          </div>
                        </div>
                      </div>
                      {!showReplyForm && (
                        <Button 
                          color="primary" 
                          onClick={() => setShowReplyForm(true)}
                          style={{ borderRadius: '6px' }}
                        >
                          <i className="pe-7s-plus me-1"></i>
                          Add Reply
                        </Button>
                      )}
                    </div>
                    {comments && comments.length > 0 ? (
                      <div className="conversation-thread" style={{
                        maxHeight: '600px',
                        overflowY: 'auto',
                        padding: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                      }}>
                        {comments.map((comment, index) => (
                          <div key={index} className="message-item mb-4" style={{
                            display: 'flex',
                            flexDirection: 'column',
                            animation: 'fadeIn 0.3s ease-in'
                          }}>
                            <div className="message-header mb-2" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '8px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold',
                                  fontSize: '16px',
                                  marginRight: '12px',
                                  flexShrink: 0
                                }}>
                                  {(comment.created_by_name || 'A').charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <strong style={{ color: '#212529', fontSize: '15px' }}>
                                    {comment.created_by_name || 'Admin'}
                                  </strong>
                                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                                    {new Date(comment.created_at).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="message-content" style={{
                              backgroundColor: '#ffffff',
                              padding: '15px 18px',
                              borderRadius: '12px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              borderLeft: '4px solid #007bff',
                              marginLeft: '52px',
                              wordWrap: 'break-word',
                              whiteSpace: 'pre-wrap'
                            }}>
                              <p style={{ margin: 0, color: '#212529', lineHeight: '1.6' }}>
                                {comment.message}
                              </p>
                              {comment.attachments && comment.attachments.length > 0 && (
                                <div className="message-attachments mt-3" style={{
                                  marginTop: '12px',
                                  paddingTop: '12px',
                                  borderTop: '1px solid #e9ecef'
                                }}>
                                  <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '8px', fontWeight: '500' }}>
                                    <i className="pe-7s-paperclip me-1"></i>
                                    Attachments:
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {comment.attachments.map((attachment, attIndex) => {
                                      // Handle Base64 format (for AddCustomerReply attachments)
                                      const isBase64 = attachment && attachment.data && attachment.data.startsWith('data:');
                                      const attachmentUrl = isBase64 
                                        ? attachment.data 
                                        : (() => {
                                            const baseUrlForFiles = process.env.REACT_APP_LOGINURL?.replace('/api/', '/') || 'http://localhost:3005/';
                                            // Handle both object format {path, filename} and string format (backward compatibility)
                                            const attachmentPath = typeof attachment === 'string' ? attachment : (attachment.path || attachment);
                                            return attachmentPath.startsWith('http') 
                                              ? attachmentPath 
                                              : `${baseUrlForFiles}${attachmentPath}`;
                                          })();
                                      const attachmentFilename = isBase64
                                        ? (attachment.filename || 'attachment')
                                        : (typeof attachment === 'string' 
                                          ? attachment.split('/').pop() 
                                          : (attachment.filename || (attachment.path ? attachment.path.split('/').pop() : 'attachment')));
                                      // Handle click for Base64 attachments (open in new window properly)
                                      const handleAttachmentClick = (e) => {
                                        if (isBase64) {
                                          e.preventDefault();
                                          // Convert Base64 to blob URL for more reliable opening
                                          try {
                                            const base64Data = attachment.data.split(',')[1];
                                            const mimeType = attachment.data.split(',')[0].match(/:(.*?);/)[1];
                                            const byteCharacters = atob(base64Data);
                                            const byteNumbers = new Array(byteCharacters.length);
                                            for (let i = 0; i < byteCharacters.length; i++) {
                                              byteNumbers[i] = byteCharacters.charCodeAt(i);
                                            }
                                            const byteArray = new Uint8Array(byteNumbers);
                                            const blob = new Blob([byteArray], { type: mimeType });
                                            const blobUrl = URL.createObjectURL(blob);
                                            const newWindow = window.open(blobUrl, '_blank');
                                            if (newWindow) {
                                              // Clean up blob URL after a delay
                                              setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
                                            }
                                          } catch (error) {
                                            console.error('Error opening Base64 attachment:', error);
                                            // Fallback: try direct open
                                            window.open(attachment.data, '_blank');
                                          }
                                        }
                                        // For non-Base64, let default link behavior work
                                      };

                                      return (
                                      <a
                                        key={attIndex}
                                        href={isBase64 ? '#' : attachmentUrl}
                                        target={isBase64 ? undefined : "_blank"}
                                        rel={isBase64 ? undefined : "noopener noreferrer"}
                                        onClick={handleAttachmentClick}
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          padding: '6px 12px',
                                          backgroundColor: '#f8f9fa',
                                          borderRadius: '6px',
                                          textDecoration: 'none',
                                          color: '#007bff',
                                          fontSize: '13px',
                                          border: '1px solid #dee2e6',
                                          transition: 'all 0.2s',
                                          cursor: 'pointer'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#e9ecef';
                                          e.currentTarget.style.borderColor = '#007bff';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                                          e.currentTarget.style.borderColor = '#dee2e6';
                                        }}
                                      >
                                        <i className="pe-7s-file me-1"></i>
                                        {attachmentFilename}
                                      </a>
                                    );
                                  })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        padding: '60px 40px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        border: '2px dashed #dee2e6',
                        color: '#6c757d'
                      }}>
                        <div style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '50%',
                          backgroundColor: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 20px',
                          border: '2px solid #e9ecef'
                        }}>
                          <i className="pe-7s-chat" style={{ fontSize: '40px', opacity: 0.4 }}></i>
                        </div>
                        <p style={{ margin: 0, fontSize: '16px', fontWeight: '500', color: '#495057' }}>
                          No messages yet
                        </p>
                        <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#6c757d' }}>
                          Start the discussion by adding a reply below
                        </p>
                      </div>
                    )}

                    {showReplyForm && (
                      <div style={{
                        marginTop: '20px',
                        padding: '20px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '15px', color: '#212529' }}>
                          <i className="pe-7s-edit me-2"></i>
                          Add Reply
                        </div>
                        <FormGroup>
                          <Label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                            Message <span style={{ color: '#dc3545' }}>*</span>
                          </Label>
                          <Input
                            type="textarea"
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            rows="5"
                            placeholder="Type your message here..."
                            style={{ borderRadius: '6px', border: '1px solid #dee2e6' }}
                          />
                        </FormGroup>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                                <i className="pe-7s-flag me-1"></i>
                                Update Status (Optional)
                              </Label>
                              <Input
                                type="select"
                                value={replyStatus}
                                onChange={(e) => setReplyStatus(e.target.value)}
                                style={{ borderRadius: '6px' }}
                              >
                                {statuses.map((status, index) => (
                                  <option key={index} value={status.value}>{status.name}</option>
                                ))}
                              </Input>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                                <i className="pe-7s-paperclip me-1"></i>
                                Attachments (Optional)
                              </Label>
                              <Input
                                type="file"
                                multiple
                                onChange={(e) => setReplyFiles(Array.from(e.target.files))}
                                style={{ borderRadius: '6px' }}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                          <Button 
                            color="success" 
                            onClick={handleAddReply}
                            style={{ borderRadius: '6px' }}
                          >
                            <i className="pe-7s-mail me-1"></i>
                            Send Reply
                          </Button>
                          <Button 
                            color="secondary" 
                            onClick={() => setShowReplyForm(false)}
                            style={{ borderRadius: '6px' }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>

                {/* Activity Log Section */}
                {complaint.activity_log && complaint.activity_log.length > 0 && (
                  <Card className="main-card mb-3">
                    <CardBody>
                      <div className="d-flex align-items-center mb-3" style={{ borderBottom: '2px solid #e9ecef', paddingBottom: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          marginRight: '12px'
                        }}>
                          <i className="pe-7s-clock"></i>
                        </div>
                        <div>
                          <h5 className="mb-0" style={{ fontWeight: '600', color: '#212529' }}>
                            Activity Log
                          </h5>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                            {complaint.activity_log.length} {complaint.activity_log.length === 1 ? 'activity' : 'activities'}
                          </div>
                        </div>
                      </div>
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <Table hover responsive style={{ marginBottom: 0 }}>
                          <thead style={{ backgroundColor: '#f8f9fa' }}>
                            <tr>
                              <th style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', color: '#6c757d', borderBottom: '2px solid #dee2e6' }}>Date & Time</th>
                              <th style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', color: '#6c757d', borderBottom: '2px solid #dee2e6' }}>Action</th>
                              <th style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', color: '#6c757d', borderBottom: '2px solid #dee2e6' }}>Performed By</th>
                              <th style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', color: '#6c757d', borderBottom: '2px solid #dee2e6' }}>Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {complaint.activity_log.map((activity, index) => (
                              <tr key={index} style={{ borderBottom: '1px solid #f1f3f5' }}>
                                <td style={{ fontSize: '13px', color: '#495057', padding: '12px', whiteSpace: 'nowrap' }}>
                                  {new Date(activity.timestamp).toLocaleString()}
                                </td>
                                <td style={{ fontSize: '13px', color: '#212529', padding: '12px', fontWeight: '500' }}>
                                  {activity.action}
                                </td>
                                <td style={{ fontSize: '13px', color: '#495057', padding: '12px' }}>
                                  {activity.performed_by_name || 'N/A'}
                                </td>
                                <td style={{ fontSize: '13px', color: '#495057', padding: '12px', lineHeight: '1.6' }}>
                                  <div style={{
                                    padding: '10px 12px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                    border: '1px solid #e9ecef'
                                  }}>
                                    {formatActivityDetails(activity.details, activity.action, activity.performed_by_name)}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </LoadingOverlay>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ViewComplaint;

